"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { Button, InputGroup, ProgressBar, Tabs } from "@heroui/react";
import { IconPhotoPlus, IconSearch, IconLink } from "@tabler/icons-react";
import { trpc } from "@/lib/trpc";
import { ensureAnonymousAuth, getMediaTypeForMedia, uploadFileDirect } from "@hs/shared";
import { SelectMyImageGalleryPanel } from "@/components/SelectMyImage";

const CLOUDBASE_ENV_ID = "his-1g5d6vlz6368064f";

const POPOVER_GAP = 8;
const VIEW_MARGIN = 12;
/** 锚点下方可用高度低于此值时，若上方更宽则翻到上方展示 */
const MIN_COMFORT_SPACE_BELOW = 280;
/** 与触发区同宽；过窄时保证图库三列可用 */
const MIN_POPOVER_WIDTH = 280;

type TabKey = "gallery" | "upload" | "link";

const TAB_GALLERY: TabKey = "gallery";
const TAB_UPLOAD: TabKey = "upload";
const TAB_LINK: TabKey = "link";

type UploadTabState =
  | { status: "idle" }
  | { status: "uploading"; file: File; percent: number }
  | { status: "error"; file: File; message: string };

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

const checkerboardStyle: React.CSSProperties = {
  backgroundColor: "#e5e5e5",
  backgroundImage: [
    "linear-gradient(45deg, #d4d4d4 25%, transparent 5%)",
    "linear-gradient(-45deg, #d4d4d4 25%, transparent 5%)",
    "linear-gradient(45deg, transparent 75%, #d4d4d4 75%)",
    "linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)",
  ].join(", "),
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
};

export type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
} | null;

async function normalizeHref(raw: string): Promise<string> {
  const href = raw.trim();
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  return `http://${href}`;
}

export function ImageSelectPopover({
  open,
  onOpenChange,
  anchorRect,
  onPick,
  defaultTab = "gallery",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRect: AnchorRect;
  onPick: (src: string) => void;
  defaultTab?: TabKey;
}) {
  const [tab, setTab] = React.useState<TabKey>(defaultTab);
  const [searchKeyword, setSearchKeyword] = React.useState("");

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const galleryScrollRef = React.useRef<HTMLDivElement | null>(null);

  const [uploadTabState, setUploadTabState] = React.useState<UploadTabState>({ status: "idle" });
  const uploadPreviewFile =
    uploadTabState.status === "uploading" || uploadTabState.status === "error"
      ? uploadTabState.file
      : null;
  const uploadObjectUrl = useObjectUrl(uploadPreviewFile);

  const [linkUrl, setLinkUrl] = React.useState("");
  /** 在「我的图库」下记录弹层高度；切到上传/链接时用作 minHeight，避免整体变矮 */
  const [galleryPopoverMinHeight, setGalleryPopoverMinHeight] = React.useState(0);

  React.useEffect(() => {
    if (!open) {
      setGalleryPopoverMinHeight(0);
      return;
    }
    setTab(defaultTab);
    setSearchKeyword("");
    setUploadTabState({ status: "idle" });
    setLinkUrl("");
  }, [open, defaultTab]);

  const utils = trpc.useUtils();
  const authorizeUploadMutation = trpc.assets.authorizeUpload.useMutation();
  const createMediaMutation = trpc.assets.create.useMutation();

  /** 离开图库前同步量一次，避免仅用 layout effect 时记录到偏小的高度（骨架屏 / 异步布局） */
  const recordGalleryShellHeight = React.useCallback(() => {
    const el = popoverRef.current;
    if (!el) return;
    const h = Math.round(el.getBoundingClientRect().height);
    if (h <= 0) return;
    setGalleryPopoverMinHeight((m) => Math.max(m, h));
  }, []);

  React.useEffect(() => {
    if (!open || tab !== TAB_GALLERY) return;
    const el = popoverRef.current;
    if (!el) return;
    const sync = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h <= 0) return;
      setGalleryPopoverMinHeight((m) => Math.max(m, h));
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, tab]);

  const handleTabValueChange = React.useCallback(
    (v: string) => {
      const next = v as TabKey;
      if (tab === TAB_GALLERY && next !== TAB_GALLERY) {
        recordGalleryShellHeight();
      }
      setTab(next);
    },
    [recordGalleryShellHeight, tab],
  );

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest('[data-image-picker-trigger="true"]')) return;
      if (t.closest('[data-slot="dialog-content"], [data-slot="dialog-overlay"], [data-image-crop-dialog="true"]')) return;
      if (popoverRef.current && popoverRef.current.contains(t)) return;
      onOpenChange(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, onOpenChange]);

  const handlePick = React.useCallback(
    (src: string) => {
      if (!src) return;
      onPick(src);
      onOpenChange(false);
      setUploadTabState({ status: "idle" });
    },
    [onOpenChange, onPick]
  );

  const handleUploadClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) return;

      e.target.value = "";
      setUploadTabState({ status: "uploading", file, percent: 0 });
      try {
        const { objectKey } = await authorizeUploadMutation.mutateAsync({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileSize: file.size,
        });
        await ensureAnonymousAuth(CLOUDBASE_ENV_ID);
        const result = await uploadFileDirect(
          { envId: CLOUDBASE_ENV_ID },
          file,
          {
            path: objectKey,
            onProgress: (p) => {
              setUploadTabState((prev) =>
                prev.status === "uploading" ? { ...prev, percent: p.percent } : prev
              );
            },
          }
        );
        const media = await createMediaMutation.mutateAsync({
          name: file.name,
          fileId: result.fileId,
          fileSize: file.size,
          mimeType: file.type || "application/octet-stream",
          type: getMediaTypeForMedia(file.type, file.name),
          isOwner: true,
        });
        void utils.assets.listMy.invalidate();
        setUploadTabState({ status: "idle" });
        handlePick((media as { url?: string | null }).url ?? "");
      } catch (err) {
        setUploadTabState({
          status: "error",
          file,
          message: err instanceof Error ? err.message : "上传失败",
        });
      }
    },
    [authorizeUploadMutation, createMediaMutation, handlePick, utils.assets.listMy]
  );

  const handleLinkConfirm = React.useCallback(async () => {
    const next = await normalizeHref(linkUrl);
    if (!next) return;
    handlePick(next);
  }, [handlePick, linkUrl]);

  const popoverStyle = React.useMemo<React.CSSProperties>(() => {
    if (!anchorRect) return { display: "none" };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const widthPx = Math.min(
      Math.max(anchorRect.width, MIN_POPOVER_WIDTH),
      vw - 2 * VIEW_MARGIN,
    );
    const left = Math.min(anchorRect.left, vw - widthPx - VIEW_MARGIN);
    const anchorBottom = anchorRect.top + anchorRect.height;
    const spaceBelow = vh - anchorBottom - POPOVER_GAP - VIEW_MARGIN;
    const spaceAbove = anchorRect.top - POPOVER_GAP - VIEW_MARGIN;

    const placeAbove =
      spaceBelow < MIN_COMFORT_SPACE_BELOW && spaceAbove > spaceBelow;

    const base: React.CSSProperties = {
      left,
      width: widthPx,
      maxWidth: "calc(100vw - 24px)",
    };

    const capHeight = (available: number) =>
      Math.min(Math.max(0, available), vh - 2 * VIEW_MARGIN);

    if (placeAbove) {
      return {
        ...base,
        top: "auto",
        bottom: vh - anchorRect.top + POPOVER_GAP,
        maxHeight: capHeight(spaceAbove),
      };
    }

    return {
      ...base,
      top: anchorBottom + POPOVER_GAP,
      bottom: "auto",
      maxHeight: capHeight(spaceBelow),
    };
  }, [anchorRect]);

  /** 图库 Tab 下也需锁定高度，否则搜索导致结果变少时弹层会随内容变矮 */
  const shellStyle = React.useMemo<React.CSSProperties>(() => {
    if (galleryPopoverMinHeight <= 0) return popoverStyle;
    return { ...popoverStyle, minHeight: galleryPopoverMinHeight };
  }, [galleryPopoverMinHeight, popoverStyle]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[1200] flex min-h-0 flex-col overflow-hidden rounded-xl border border-divider bg-surface shadow-xl"
      style={shellStyle}
    >
      <Tabs
        selectedKey={tab}
        onSelectionChange={(key) => {
          if (key == null) return;
          handleTabValueChange(String(key));
        }}
        orientation="horizontal"
        className="flex min-h-0 w-full min-w-0 flex-1 flex-col"
      >
        <Tabs.ListContainer className="shrink-0 border-b border-divider/60 px-6 pb-3 pt-4">
          <div className="flex min-w-0 items-center gap-3">
            <Tabs.List aria-label="图片来源" className="grid min-h-8 min-w-0 flex-1 grid-cols-3 gap-1">
              <Tabs.Tab id={TAB_GALLERY}>
                我的图库
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id={TAB_UPLOAD}>
                上传
                <Tabs.Indicator />
              </Tabs.Tab>
              <Tabs.Tab id={TAB_LINK}>
                链接
                <Tabs.Indicator />
              </Tabs.Tab>
            </Tabs.List>
            <div className="flex min-h-9 min-w-0 flex-1 items-center">
              {tab === TAB_GALLERY ? (
                <InputGroup className="min-w-0 w-full">
                  <InputGroup.Prefix className="pointer-events-none text-muted">
                    <IconSearch className="size-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input
                    placeholder="搜索图片名称"
                    value={searchKeyword}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.target.value)}
                  />
                </InputGroup>
              ) : null}
            </div>
          </div>
        </Tabs.ListContainer>

        <div
          ref={galleryScrollRef}
          className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
        >
          <Tabs.Panel id={TAB_GALLERY} className="p-6 pt-4">
            <SelectMyImageGalleryPanel
              active={open && tab === TAB_GALLERY}
              listHostOpen={open}
              searchKeyword={searchKeyword}
              onPickImage={handlePick}
              scrollRootRef={galleryScrollRef}
            />
          </Tabs.Panel>

          <Tabs.Panel
            id={TAB_UPLOAD}
            className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex min-h-[min(280px,45vh)] flex-1 flex-col">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border-2 border-dashed border-divider/70 bg-default-100/60">
                {uploadTabState.status === "idle" ? (
                  <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 p-6">
                    <IconPhotoPlus className="size-10 text-muted" />
                    <Button type="button" variant="primary" onPress={handleUploadClick}>
                      点击上传图片
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      className="relative min-h-[160px] flex-1 overflow-hidden"
                      style={checkerboardStyle}
                    >
                      {uploadObjectUrl ? (
                        <img
                          src={uploadObjectUrl}
                          alt={uploadTabState.file.name}
                          className="absolute inset-0 size-full object-contain"
                        />
                      ) : (
                        <div className="flex size-full items-center justify-center text-xs text-muted">准备中…</div>
                      )}
                    </div>
                    <div className="shrink-0 space-y-2 border-t border-divider/60 bg-surface/90 px-4 py-3">
                      <div className="truncate text-xs text-foreground">{uploadTabState.file.name}</div>
                      {uploadTabState.status === "uploading" ? (
                        <ProgressBar
                          aria-label="上传进度"
                          value={uploadTabState.percent}
                          minValue={0}
                          maxValue={100}
                          className="w-full"
                        >
                          <ProgressBar.Track className="h-1.5 rounded-full bg-default-200">
                            <ProgressBar.Fill className="h-full rounded-full bg-primary" />
                          </ProgressBar.Track>
                        </ProgressBar>
                      ) : (
                        <p className="text-xs text-danger">{uploadTabState.message}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {uploadTabState.status === "error" ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onPress={() => setUploadTabState({ status: "idle" })}
                          >
                            关闭
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant={uploadTabState.status === "error" ? "primary" : "secondary"}
                          size="sm"
                          onPress={handleUploadClick}
                          isDisabled={uploadTabState.status === "uploading"}
                        >
                          {uploadTabState.status === "uploading" ? "上传中…" : "重新选择"}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Tabs.Panel>

          <Tabs.Panel id={TAB_LINK} className="p-6 pt-4">
            <div className="flex flex-col gap-3">
              <InputGroup className="w-full">
                <InputGroup.Prefix className="pointer-events-none text-muted">
                  <IconLink className="size-4" />
                </InputGroup.Prefix>
                <InputGroup.Input
                  placeholder="粘贴图片网址"
                  value={linkUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLinkUrl(e.target.value)}
                />
              </InputGroup>
              <Button
                type="button"
                variant="primary"
                className="w-fit"
                onPress={() => void handleLinkConfirm()}
                isDisabled={!linkUrl.trim()}
              >
                确定
              </Button>
            </div>
          </Tabs.Panel>
        </div>
      </Tabs>
    </div>,
    document.body
  );
}

